/**
 * O financeiro gerencial (módulo 15; 0102): o que a mesa e o celular mostram e
 * escrevem, sem rede, para o teste alcançar.
 *
 * Nenhuma conta da DRE mora aqui: o número, as premissas, a frase fiscal e a
 * conferência vêm do servidor. Aqui está só o que é da tela — o período no
 * endereço, o nome de cada recorte, a variação contra o período anterior com
 * o sentido certo, o arquivo exportado e o retorno das gravações (R04).
 */
import { formatarCompetencia, formatarDia, formatarReais } from '@/lib/formato';
import type { RetornoDeclarado } from '@/lib/retorno';
import { variacaoDoPeriodo, type VariacaoDoCard } from '@/lib/variacao';

import type { ContaDoPlano, Grupo, LancamentoGerencial, NovaConta, Recorte, SituacaoDoTitulo } from './api';

/* ----------------------------------------------------- o endereço da tela */

export type AbaDoFinanceiro = 'dre' | 'plano' | 'titulos';
export const PERIODOS_DA_DRE = [1, 3, 12] as const;
export type PeriodoDaDre = (typeof PERIODOS_DA_DRE)[number];
export const RECORTES: readonly Recorte[] = ['empresa', 'contrato', 'cliente', 'obra', 'centro', 'equipamento'];

export interface BuscaDoFinanceiro {
  readonly aba?: 'plano' | 'titulos';
  /** Meses até a competência final; ausente = 12. */
  readonly meses?: 1 | 3;
  /** A competência final, `aaaa-mm`; ausente = a corrente. */
  readonly ate?: string;
  readonly recorte?: Exclude<Recorte, 'empresa'>;
}

export function validarBuscaDoFinanceiro(entrada: Record<string, unknown>): BuscaDoFinanceiro {
  const aba = entrada['aba'];
  const meses = Number(entrada['meses']);
  const ate = entrada['ate'];
  const recorte = entrada['recorte'];
  return {
    ...(aba === 'plano' || aba === 'titulos' ? { aba } : {}),
    ...(meses === 1 || meses === 3 ? { meses } : {}),
    ...(typeof ate === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(ate) ? { ate } : {}),
    ...(typeof recorte === 'string' && RECORTES.includes(recorte as Recorte) && recorte !== 'empresa'
      ? { recorte: recorte as Exclude<Recorte, 'empresa'> }
      : {}),
  };
}

/** `aaaa-mm-01` do mês de `quando`, no relógio de quem olha. */
export function competenciaDe(quando: Date): string {
  return `${String(quando.getFullYear())}-${String(quando.getMonth() + 1).padStart(2, '0')}-01`;
}

/** `aaaa-mm-01` do mês `n` meses depois (ou antes). */
export function somarMeses(competencia: string, n: number): string {
  const [ano, mes] = competencia.split('-').map(Number) as [number, number];
  const total = ano * 12 + (mes - 1) + n;
  return `${String(Math.floor(total / 12))}-${String((total % 12) + 1).padStart(2, '0')}-01`;
}

/** A janela da DRE: `meses` competências terminando em `ate`. */
export function janelaDaDre(meses: PeriodoDaDre, ate: string): { readonly de: string; readonly ate: string } {
  const fim = `${ate.slice(0, 7)}-01`;
  return { de: somarMeses(fim, -(meses - 1)), ate: fim };
}

/** As últimas `n` competências até `ate`, da mais recente para a mais antiga (a lista do seletor). */
export function competenciasRecentes(ate: string, n = 24): string[] {
  return Array.from({ length: n }, (_, i) => somarMeses(`${ate.slice(0, 7)}-01`, -i));
}

export function periodoEscrito(meses: PeriodoDaDre, de: string, ate: string): string {
  return meses === 1 ? `Competência ${formatarCompetencia(ate)}` : `${formatarCompetencia(de)} a ${formatarCompetencia(ate)}`;
}

/* -------------------------------------------------- os nomes de cada coisa */

export const NOME_DO_RECORTE: Readonly<Record<Recorte, string>> = {
  empresa: 'Empresa',
  contrato: 'Contrato',
  cliente: 'Cliente',
  obra: 'Obra',
  centro: 'Centro de custo',
  equipamento: 'Equipamento',
};

export const NOME_DO_GRUPO: Readonly<Record<Grupo, string>> = {
  receita: 'Receita',
  custo_direto: 'Custo direto',
  custo_indireto: 'Custo indireto',
  depreciacao: 'Depreciação',
};

export const GRUPOS: readonly Grupo[] = ['receita', 'custo_direto', 'custo_indireto', 'depreciacao'];

export const SITUACAO_DO_TITULO: Readonly<Record<SituacaoDoTitulo, string>> = {
  a_receber: 'A receber',
  recebido_a_menor: 'Recebido a menor',
  recebido: 'Recebido',
};

export const SITUACAO_DA_FATURA: Readonly<Record<'aberta' | 'conciliada' | 'divergente', string>> = {
  aberta: 'Aberta',
  conciliada: 'Conciliada',
  divergente: 'Divergente',
};

/** "R$ 1.234,56", e o negativo com o sinal de menos tipográfico. */
export function reaisComSinal(valor: number): string {
  return valor < 0 ? `−${formatarReais(Math.abs(valor))}` : formatarReais(valor);
}

/** "29,3%", ou o travessão sem receita — nunca "0%" de quem só não tem medição. */
export function margemEscrita(margem: number | null | undefined): string {
  if (margem === null || margem === undefined) return '—';
  return `${margem < 0 ? '−' : ''}${Math.abs(margem).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

/* ---------------------------------------------------------- a variação */

/**
 * A variação da linha contra o período anterior, com o sentido do GRUPO:
 * receita que sobe melhorou; custo que sobe piorou. Sem base, nada (R02).
 */
export function variacaoDaLinha(grupo: Grupo | 'resultado', atual: number, anterior: number): VariacaoDoCard | null {
  return variacaoDoPeriodo(atual, anterior, grupo === 'receita' || grupo === 'resultado' ? 'subir_e_melhor' : 'subir_e_pior');
}

/* ------------------------------------------------------ a conferência */

/** A frase que diz contra o que a DRE foi conferida, ao centavo. */
export function fraseDaConferencia(c: {
  readonly receita: { readonly dre: number; readonly origem: number };
  readonly custoTotal: { readonly dre: number; readonly origem: number } | null;
  readonly fecha: boolean;
}): string {
  const custo = c.custoTotal ? ` e o custo total da tela de Custos (${formatarReais(c.custoTotal.origem)})` : '';
  return c.fecha
    ? `Fecha ao centavo com a receita das medições aprovadas (${formatarReais(c.receita.origem)})${custo}.`
    : `Não fecha com as telas de origem: a DRE soma ${formatarReais(c.receita.dre)} de receita${c.custoTotal ? ` e ${formatarReais(c.custoTotal.dre)} de custo` : ''}. Avise o suporte com o período: é defeito, e não dado.`;
}

/* ------------------------------------------------------ o formulário */

export interface ValoresDaConta {
  readonly codigo: string;
  readonly nome: string;
  readonly grupo: Grupo | '';
  readonly contaContabil: string;
}

export function faltaNaConta(v: ValoresDaConta, contas: readonly ContaDoPlano[] = [], editando: string | null = null): Partial<Record<keyof ValoresDaConta, string>> {
  const falta: Partial<Record<keyof ValoresDaConta, string>> = {};
  const codigo = v.codigo.trim();
  if (!/^[0-9]+(\.[0-9]+)*$/.test(codigo)) falta.codigo = 'Escreva o código com números separados por ponto, por exemplo 3.2.';
  else if (contas.some((c) => c.codigo === codigo && c.id !== editando)) falta.codigo = `O código ${codigo} já é de outra conta. Escolha outro.`;
  if (v.nome.trim().length < 3) falta.nome = 'Escreva o nome da conta, por exemplo "Seguros da frota".';
  if (v.grupo === '') falta.grupo = 'Escolha o grupo da DRE em que a conta aparece.';
  return falta;
}

export function corpoDaConta(v: ValoresDaConta): NovaConta {
  return { codigo: v.codigo.trim(), nome: v.nome.trim(), grupo: v.grupo as Grupo, contaContabil: v.contaContabil.trim() || null };
}

/** O próximo código livre do grupo: o maior "n.x" + 1. */
export function proximoCodigo(contas: readonly ContaDoPlano[], grupo: Grupo): string {
  const raiz = String(GRUPOS.indexOf(grupo) + 1);
  const usados = contas
    .filter((c) => c.codigo.split('.')[0] === raiz)
    .map((c) => Number(c.codigo.split('.')[1] ?? 0))
    .filter(Number.isFinite);
  return `${raiz}.${String(Math.max(0, ...usados) + 1)}`;
}

/* ------------------------------------------------------ os retornos (R04) */
/* O TÍTULO é escrito em cada gravação (o teste `retorno-das-gravacoes` cobra o
   `titulo:` dentro do `retornoDe`); aqui mora o resto — o alvo, os fatos e o
   efeito —, que a mesa e o celular declaram igual. */

type DetalheDoRetorno = Omit<RetornoDeclarado, 'titulo'>;

export function detalheDaContaCriada(r: { readonly codigo: string; readonly nome: string; readonly grupo: Grupo }): DetalheDoRetorno {
  return {
    alvo: { codigo: r.codigo, nome: r.nome },
    fatos: [NOME_DO_GRUPO[r.grupo]],
    efeito: 'Aparece na DRE quando uma origem for classificada nela',
  };
}

interface ContaAlteradaNaResposta {
  readonly codigo: string;
  readonly nome: string;
  readonly ativo: boolean;
  readonly antes: { readonly ativo: boolean; readonly codigo: string };
}

/** "Conta desativada", "Conta reativada" ou "Conta do plano gerencial alterada". */
export function tituloDaContaAlterada(r: ContaAlteradaNaResposta): string {
  if (r.antes.ativo === r.ativo) return 'Conta do plano gerencial alterada';
  return r.ativo ? 'Conta reativada' : 'Conta desativada';
}

export function detalheDaContaAlterada(r: ContaAlteradaNaResposta, motivo?: string): DetalheDoRetorno {
  const mudouUso = r.antes.ativo !== r.ativo;
  return {
    alvo: { codigo: r.codigo, nome: r.nome },
    fatos: [r.antes.codigo !== r.codigo ? `código ${r.antes.codigo} → ${r.codigo}` : null, motivo?.trim() ? `motivo: ${motivo.trim()}` : null],
    ...(mudouUso ? { efeito: { rotulo: 'Situação', antes: r.antes.ativo ? 'Em uso' : 'Desativada', depois: r.ativo ? 'Em uso' : 'Desativada' } } : {}),
  };
}

interface ClassificacaoNaResposta {
  readonly antes: string | null;
  readonly depois: { readonly codigo: string; readonly nome: string } | null;
}

export function tituloDaClassificacao(r: ClassificacaoNaResposta): string {
  return r.depois ? 'Origem classificada no plano' : 'Exceção do centro retirada';
}

export function detalheDaClassificacao(r: ClassificacaoNaResposta, rotuloDaOrigem: string, motivo: string): DetalheDoRetorno {
  return {
    alvo: { nome: rotuloDaOrigem },
    fatos: [r.depois ? `conta ${r.depois.codigo} · ${r.depois.nome}` : 'volta à regra geral do custo indireto', `motivo: ${motivo.trim()}`],
    efeito: { rotulo: 'Conta na DRE', antes: r.antes ?? 'regra geral', depois: r.depois?.codigo ?? 'regra geral' },
  };
}

export function detalheDaValidacao(r: { readonly validadoEm: string; readonly contador: string; readonly contas: number }): DetalheDoRetorno {
  return {
    alvo: { nome: r.contador },
    fatos: [`em ${formatarDia(r.validadoEm)}`, `${String(r.contas)} ${r.contas === 1 ? 'conta' : 'contas'} em uso`],
    efeito: 'O plano de hoje fica guardado; o que mudar depois aparece na tela',
  };
}

/* ---------------------------------------------- o arquivo dos lançamentos */

const RODAPE_DA_EXPORTACAO = 'Contrato Terraplanagem · Sistema desenvolvido pela Atenas';

/**
 * Os lançamentos gerenciais de uma competência em CSV, com ponto e vírgula e
 * vírgula decimal (o que a planilha brasileira abre sem perguntar). É o ponto
 * de extensão do módulo 04: a conta contábil de cada linha liga ao ERP.
 */
export function csvDosLancamentos(lancamentos: readonly LancamentoGerencial[], escrituracao: string): string {
  const campo = (v: string | null) => {
    const t = v ?? '';
    return /[;"\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
  };
  const linhas = [
    ['Competência', 'Conta', 'Nome da conta', 'Conta contábil', 'Grupo', 'Equipamento', 'Contrato', 'Valor (R$)'].join(';'),
    ...lancamentos.map((l) =>
      [
        formatarCompetencia(l.competencia),
        l.contaCodigo,
        l.contaNome,
        l.contaContabil,
        l.grupo ? NOME_DO_GRUPO[l.grupo] : null,
        l.equipamento,
        l.contrato,
        l.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false }),
      ]
        .map(campo)
        .join(';'),
    ),
    '',
    campo(escrituracao),
    RODAPE_DA_EXPORTACAO,
  ];
  return `\uFEFF${linhas.join('\n')}\n`;
}
